import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { useUpdateAssignment, getLotForAssignment, type VesselAssignmentWithRelations, type UpdateAssignmentInput } from "@/hooks/useVesselAssignments";
import { useVolumeUnit } from "@/hooks/useOrganization";
import { usePicklistValues } from "@/hooks/usePicklistValues";

interface AssignmentEditPopoverProps {
  assignment: VesselAssignmentWithRelations | null;
  /** Bounding rect of the bar that was double-clicked */
  anchorRect: DOMRect | null;
  onClose: () => void;
  onMarkReceived?: (assignment: VesselAssignmentWithRelations) => void;
  /** Called when the user saves changes; parent owns the mutation + undo push */
  onCommit?: (id: string, after: UpdateAssignmentInput, before: UpdateAssignmentInput, lotCode: string) => void;
}


const POPOVER_WIDTH = 272;
const POPOVER_HEIGHT_ESTIMATE = 280; // label + in date + out date + volume + out reason + notes rows + padding
const MARGIN = 8;

function getPopoverPosition(rect: DOMRect): { top: number; left: number } {
  const viewportW = window.innerWidth;
  const viewportH = window.innerHeight;

  // Prefer above the bar; fall back to below
  let top = rect.top - POPOVER_HEIGHT_ESTIMATE - MARGIN;
  if (top < MARGIN) top = rect.bottom + MARGIN;
  // Clamp vertically
  top = Math.min(top, viewportH - POPOVER_HEIGHT_ESTIMATE - MARGIN);

  // Center horizontally on the bar; clamp to viewport
  let left = rect.left + rect.width / 2 - POPOVER_WIDTH / 2;
  left = Math.max(MARGIN, Math.min(left, viewportW - POPOVER_WIDTH - MARGIN));

  return { top, left };
}

export function AssignmentEditPopover({ assignment, anchorRect, onClose, onMarkReceived, onCommit }: AssignmentEditPopoverProps) {
  const updateAssignment = useUpdateAssignment();
  const { volumeAbbrev } = useVolumeUnit();
  const { data: emptyReasonOptions = [] } = usePicklistValues("empty_reason");

  const [label, setLabel] = useState("");
  const [volume, setVolume] = useState("");
  const [outReason, setOutReason] = useState<string>("");
  const [fillDate, setFillDate] = useState("");
  const [projectedEmptyDate, setProjectedEmptyDate] = useState("");
  const [notes, setNotes] = useState("");

  const popoverRef = useRef<HTMLDivElement>(null);
  const labelInputRef = useRef<HTMLInputElement>(null);
  // Track whether values have changed so we don't fire unnecessary mutations
  const savedRef = useRef(false);
  // Only overwrite display_label if the user actually edited the field this session —
  // otherwise a stale `label` snapshot vs. a freshly re-fetched lot code gets misread as an edit.
  const labelTouchedRef = useRef(false);

  // Sync state when assignment changes (new popover open)
  useEffect(() => {
    if (!assignment) return;
    const lot = getLotForAssignment(assignment);
    setLabel(assignment.display_label ?? lot?.code ?? "");
    setVolume(assignment.fill_volume_l != null ? String(Math.round(assignment.fill_volume_l)) : "");
    setOutReason(assignment.empty_reason ?? "");
    setFillDate(assignment.fill_date ?? "");
    setProjectedEmptyDate(assignment.projected_empty_date ?? "");
    setNotes(assignment.notes ?? "");
    savedRef.current = false;
    labelTouchedRef.current = false;
    // Focus label input on open
    setTimeout(() => labelInputRef.current?.focus(), 0);
  }, [assignment?.id]);

  const save = () => {
    if (!assignment || savedRef.current) return;
    savedRef.current = true;

    const lot = getLotForAssignment(assignment);
    const parsedVolume = volume !== "" ? parseFloat(volume) : null;
    const newDisplayLabel = !labelTouchedRef.current
      ? assignment.display_label ?? null
      : label.trim() === (lot?.code ?? "") ? null : label.trim() || null;
    const newVolume = !isNaN(parsedVolume!) && parsedVolume !== null ? parsedVolume : assignment.fill_volume_l;
    const newEmptyReason = outReason !== "" ? outReason : assignment.empty_reason ?? null;
    const newFillDate = fillDate || assignment.fill_date;
    const newProjectedEmptyDate = projectedEmptyDate || null;
    const newNotes = notes.trim() || null;

    const changed =
      newDisplayLabel !== (assignment.display_label ?? null) ||
      newVolume !== assignment.fill_volume_l ||
      (newEmptyReason ?? null) !== (assignment.empty_reason ?? null) ||
      newFillDate !== assignment.fill_date ||
      newProjectedEmptyDate !== (assignment.projected_empty_date ?? null) ||
      (newNotes ?? null) !== (assignment.notes ?? null);

    if (changed) {
      const before: UpdateAssignmentInput = {
        display_label: assignment.display_label ?? null,
        fill_volume_l: assignment.fill_volume_l,
        empty_reason: assignment.empty_reason ?? null,
        fill_date: assignment.fill_date,
        projected_empty_date: assignment.projected_empty_date ?? null,
        notes: assignment.notes ?? null,
      };
      const after: UpdateAssignmentInput = {
        display_label: newDisplayLabel,
        fill_volume_l: newVolume,
        empty_reason: newEmptyReason,
        fill_date: newFillDate,
        projected_empty_date: newProjectedEmptyDate,
        notes: newNotes,
      };
      if (onCommit) {
        onCommit(assignment.id, after, before, lot?.code ?? "lot");
      } else {
        updateAssignment.mutate({ id: assignment.id, input: after });
      }
    }
    onClose();
  };

  const cancel = () => {
    savedRef.current = true; // suppress save-on-close
    onClose();
  };

  // Click outside → save
  useEffect(() => {
    if (!assignment) return;
    const handlePointerDown = (e: PointerEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        save();
      }
    };
    // Use capture so we intercept before anything else
    document.addEventListener("pointerdown", handlePointerDown, true);
    return () => document.removeEventListener("pointerdown", handlePointerDown, true);
  }, [assignment, label, volume, outReason, fillDate, projectedEmptyDate, notes]);

  // Escape → cancel; Enter in any field → save
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      cancel();
    } else if (e.key === "Enter") {
      e.preventDefault();
      save();
    }
  };

  if (!assignment || !anchorRect) return null;

  const lot = getLotForAssignment(assignment);
  const { top, left } = getPopoverPosition(anchorRect);

  return (
    <div
      ref={popoverRef}
      className={cn(
        "fixed z-[200] bg-popover border rounded-lg shadow-xl",
        "animate-in fade-in zoom-in-95 duration-100",
      )}
      style={{ top, left, width: POPOVER_WIDTH }}
      onKeyDown={handleKeyDown}
    >
      {/* Label row (prominent, like iCal title) */}
      <div className="px-3 pt-3 pb-2 border-b">
        <input
          ref={labelInputRef}
          type="text"
          value={label}
          onChange={e => {
            labelTouchedRef.current = true;
            setLabel(e.target.value);
          }}
          placeholder={lot?.code ?? "Lot label"}
          className={cn(
            "w-full text-sm font-semibold bg-transparent outline-none",
            "placeholder:text-muted-foreground/50",
          )}
        />
      </div>

      {/* Fields */}
      <div className="px-3 py-2 space-y-2">
        {/* Volume */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground w-16 shrink-0">Volume</span>
          <div className="flex items-center gap-1 flex-1">
            <input
              type="number"
              value={volume}
              onChange={e => setVolume(e.target.value)}
              placeholder="—"
              className={cn(
                "flex-1 text-sm bg-muted/40 rounded px-2 py-0.5 outline-none",
                "focus:ring-1 focus:ring-primary/50",
                "min-w-0",
              )}
            />
            <span className="text-xs text-muted-foreground shrink-0">{volumeAbbrev}</span>
          </div>
        </div>

        {/* Out Reason */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground w-16 shrink-0">Out reason</span>
          <select
            value={outReason}
            onChange={e => setOutReason(e.target.value)}
            className={cn(
              "flex-1 text-sm bg-muted/40 rounded px-2 py-0.5 outline-none",
              "focus:ring-1 focus:ring-primary/50",
            )}
          >
            <option value="">—</option>
            {emptyReasonOptions.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {/* In date */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground w-16 shrink-0">In</span>
          <input
            type="date"
            value={fillDate}
            onChange={e => setFillDate(e.target.value)}
            className={cn(
              "flex-1 text-sm bg-muted/40 rounded px-2 py-0.5 outline-none",
              "focus:ring-1 focus:ring-primary/50",
            )}
          />
        </div>

        {/* Out date */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground w-16 shrink-0">Out</span>
          <input
            type="date"
            value={projectedEmptyDate}
            onChange={e => setProjectedEmptyDate(e.target.value)}
            className={cn(
              "flex-1 text-sm bg-muted/40 rounded px-2 py-0.5 outline-none",
              "focus:ring-1 focus:ring-primary/50",
            )}
          />
        </div>

        {/* Notes */}
        <div className="flex items-start gap-2">
          <span className="text-xs text-muted-foreground w-16 shrink-0 pt-1">Notes</span>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="—"
            rows={2}
            className={cn(
              "flex-1 text-sm bg-muted/40 rounded px-2 py-0.5 outline-none resize-none",
              "focus:ring-1 focus:ring-primary/50",
            )}
          />
        </div>
      </div>

      {/* Mark as received (tentative lots only) */}
      {lot?.is_tentative && onMarkReceived && (
        <div className="px-3 pb-2 border-t pt-2">
          <button
            type="button"
            onClick={() => {
              savedRef.current = true; // suppress save-on-close
              onClose();
              onMarkReceived(assignment);
            }}
            className={cn(
              "w-full text-xs rounded px-2 py-1.5 text-left",
              "bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20",
              "transition-colors",
            )}
          >
            Mark as received…
          </button>
        </div>
      )}

      {/* Footer hint */}
      <div className="px-3 pb-2 flex justify-end">
        <span className="text-[10px] text-muted-foreground/60">Return to save · Esc to cancel</span>
      </div>
    </div>
  );
}
